<template>
  <div class="container">
    <BasicTitle :title="pageTitle" />
    <BasicSubtitle subtitle="Los campos marcados con (*) son obligatorios." />
    <div class="container-form">
      <BasicLabel label="Código" />
      <BorderInput v-model="form.code" label="Código" maxlength="20" :readonly="!isEditMode" />

      <BasicLabel label="Nombre *" />
      <BorderInput v-model="form.name" label="Nombre *" />
      <FormError :show="Boolean(errors.name)" :message="errors.name" />

      <BasicLabel label="Tipo *" />
      <BorderSelect
        v-model="form.type"
        label="Tipo *"
        :options="accountTypeOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.type)" :message="errors.type" />

      <BasicLabel label="Moneda *" />
      <BorderSelect
        v-model="form.currencyId"
        label="Moneda *"
        :options="currencyOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.currencyId)" :message="errors.currencyId" />

      <BasicLabel label="Saldo inicial *" />
      <BorderInput
        v-model="form.initialBalance"
        label="Saldo inicial *"
        type="number"
        step="0.01"
        min="0"
      />
      <FormError :show="Boolean(errors.initialBalance)" :message="errors.initialBalance" />

      <BasicLabel label="Estado *" />
      <BorderSelect
        v-model="form.status"
        label="Estado *"
        :options="accountStatusOptions"
        placeholder="Seleccione"
      />
      <FormError :show="Boolean(errors.status)" :message="errors.status" />
    </div>
    <PrimaryButton :label="primaryButtonLabel" :click="onSave" />
  </div>
</template>

<script>
import { computed, reactive, ref, onMounted, watch, onBeforeUnmount } from "vue";
import { useRouter, useRoute } from "vue-router";
import store from "../../store";
import BasicTitle from "../../widgets/title/BasicTitle";
import BasicSubtitle from "../../widgets/subtitle/BasicSubtitle";
import BasicLabel from "../../widgets/label/BasicLabel";
import BorderInput from "../../widgets/input/BorderInput";
import BorderSelect from "../../widgets/select/BorderSelect";
import FormError from "../../widgets/error/FormError";
import PrimaryButton from "../../widgets/button/PrimaryButton";
import { openMode, actions } from "../../constants";
import { toOptionList } from "../../utils/options";

const ACCOUNT_TYPES = [
  { id: "income", name: "Ingreso" },
  { id: "expense", name: "Egreso" },
];

const ACCOUNT_STATUS = [
  { id: "active", name: "Activo" },
  { id: "inactive", name: "Inactivo" },
];

const createEmptyErrors = () => ({
  name: "",
  type: "",
  currencyId: "",
  initialBalance: "",
  status: "",
});

export default {
  components: {
    BasicTitle,
    BasicSubtitle,
    BasicLabel,
    BorderInput,
    BorderSelect,
    FormError,
    PrimaryButton,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();

    const selectedBuilding = computed(() => store.state.general.selectedBuilding);
    const buildingId = computed(() => {
      const value = selectedBuilding.value;
      if (!value) return null;
      return typeof value === "object" ? value.id : value;
    });

    const isEditMode = computed(() => store.state.openMode === openMode.MODIFY);
    const pageTitle = computed(() => (isEditMode.value ? "Editar cuenta contable" : "Nueva cuenta contable"));
    const primaryButtonLabel = computed(() => (isEditMode.value ? "Actualizar" : "Guardar"));

    const form = reactive({
      id: null,
      code: "",
      name: "",
      type: "",
      currencyId: "",
      initialBalance: "",
      status: "active",
    });

    const errors = reactive(createEmptyErrors());
    const currencyOptions = computed(() =>
      toOptionList(store.state.general.currencies, {
        labelFn: (item) => [item.code, item.name].filter(Boolean).join(" - "),
      })
    );
    const accountTypeOptions = computed(() => ACCOUNT_TYPES);
    const accountStatusOptions = computed(() => ACCOUNT_STATUS);

    const selectedAccountId = computed(
      () => store.state.account.selectedAccounts?.[0]?.id || store.state.account.current?.id || null
    );

    const clearErrors = () => {
      Object.keys(errors).forEach((key) => {
        errors[key] = "";
      });
    };

    const resetForm = () => {
      form.id = null;
      form.code = "";
      form.name = "";
      form.type = "";
      form.currencyId = "";
      form.initialBalance = "";
      form.status = "active";
      clearErrors();
    };

    const populateForm = (account) => {
      if (!account) return;
      form.id = account.id ?? null;
      form.code = account.code || "";
      form.name = account.name || "";
      form.type = account.type || "";
      form.currencyId = account.currencyId ? String(account.currencyId) : "";
      form.initialBalance = account.initialBalance !== undefined ? String(account.initialBalance) : "";
      form.status = account.status || "active";
      clearErrors();
    };

    const ensureGeneralData = async () => {
      if (!store.state.general.currencies?.length) {
        await store.dispatch("GENERAL_DATA");
      }
    };

    const fetchNextCode = async (targetBuildingId) => {
      if (isEditMode.value) return;
      if (!targetBuildingId) {
        form.code = "";
        return;
      }
      const response = await store.dispatch(actions.accountActions.nextCode, {
        buildingId: targetBuildingId,
      });
      if (response?.ok && response?.data?.code) {
        form.code = response.data.code;
      } else if (response?.message) {
        store.commit("setToast", {
          type: "error",
          message: response.message,
        });
        form.code = "";
      }
    };

    const loadAccount = async () => {
      if (!isEditMode.value || !selectedAccountId.value) {
        resetForm();
        return;
      }
      store.commit("setLoading", true);
      const response = await store.dispatch(actions.accountActions.findOne, selectedAccountId.value);
      store.commit("setLoading", false);
      if (response?.ok) {
        populateForm(response.data);
      } else {
        router.push("/contabilidad/cuenta/listado");
        store.commit("setToast", {
          type: "error",
          message: response?.message || "No fue posible cargar la cuenta seleccionada.",
        });
      }
    };

    const validate = () => {
      clearErrors();
      if (!form.name) {
        errors.name = "Ingrese el nombre";
      }
      if (!form.type) {
        errors.type = "Seleccione el tipo";
      }
      if (!form.currencyId) {
        errors.currencyId = "Seleccione una moneda";
      }
      const amountValue = Number(form.initialBalance);
      if (!Number.isFinite(amountValue) || amountValue < 0) {
        errors.initialBalance = "Ingrese un monto mayor o igual a cero";
      }
      if (!form.status) {
        errors.status = "Seleccione el estado";
      }
      return !Object.values(errors).some(Boolean);
    };

    const buildPayload = () => {
      const amountValue = Number(form.initialBalance);
      return {
        buildingId: buildingId.value,
        code: form.code?.trim() || null,
        name: form.name.trim(),
        type: form.type,
        currencyId: Number(form.currencyId),
        initialBalance: Number.isFinite(amountValue) ? amountValue : 0,
        status: form.status,
      };
    };

    const onSave = async () => {
      if (!validate()) return;
      if (!buildingId.value) {
        store.commit("setToast", {
          type: "error",
          message: "Seleccione un edificio para continuar.",
        });
        return;
      }

      store.commit("setLoading", true);
      try {
        const payload = buildPayload();
        let response;
        if (isEditMode.value && form.id) {
          response = await store.dispatch(actions.accountActions.update, {
            id: form.id,
            ...payload,
          });
        } else {
          response = await store.dispatch(actions.accountActions.create, payload);
        }
        if (response?.ok) {
          store.commit("setToast", {
            type: "success",
            message: isEditMode.value
              ? "Cuenta actualizada correctamente."
              : "Cuenta creada correctamente.",
          });
          store.commit("setSelectedAccounts", []);
          router.push("/contabilidad/cuenta/listado");
        } else {
          store.commit("setToast", {
            type: "error",
            message: response?.message || "No fue posible guardar la cuenta.",
          });
        }
      } catch (error) {
        console.error(error);
        store.commit("setToast", {
          type: "error",
          message: "Ocurrió un error al guardar la cuenta.",
        });
      } finally {
        store.commit("setLoading", false);
      }
    };

    const addBreadcrumb = () => {
      const exists = store.state.general.breadcrumbs.find((item) => item.to === route.path);
      if (!exists) {
        const label = isEditMode.value ? "Modificar cuenta contable" : "Nueva cuenta contable";
        store.commit("addBreadcrumb", { label, to: route.path });
      }
    };

    onMounted(async () => {
      store.commit("setLoading", true);
      try {
        await ensureGeneralData();
        if (isEditMode.value) {
          await loadAccount();
        } else {
          resetForm();
          await fetchNextCode(buildingId.value);
        }
        addBreadcrumb();
      } finally {
        store.commit("setLoading", false);
      }
    });

    onBeforeUnmount(() => {
      store.commit("removeBreadcrumb");
    });

    watch(
      buildingId,
      async (value, oldValue) => {
        if (isEditMode.value) return;
        if (value && value !== oldValue) {
          resetForm();
          await fetchNextCode(value);
        }
        if (!value) {
          form.code = "";
        }
      }
    );

    watch(
      () => form.name,
      () => {
        if (form.name) errors.name = "";
      }
    );

    watch(
      () => form.type,
      () => {
        if (form.type) errors.type = "";
      }
    );

    watch(
      () => form.currencyId,
      () => {
        if (form.currencyId) errors.currencyId = "";
      }
    );

    watch(
      () => form.initialBalance,
      () => {
        const amountValue = Number(form.initialBalance);
        if (Number.isFinite(amountValue) && amountValue >= 0) {
          errors.initialBalance = "";
        }
      }
    );

    watch(
      () => form.status,
      () => {
        if (form.status) errors.status = "";
      }
    );

    return {
      store,
      form,
      errors,
      pageTitle,
      primaryButtonLabel,
      currencyOptions,
      accountTypeOptions,
      accountStatusOptions,
      onSave,
    };
  },
};
</script>

<style scoped>
.container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.container-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 2rem 0;
  width: 100%;
  max-width: 600px;
}

.container-form > * {
  width: 100%;
  margin-bottom: 1.5rem;
}

.container-form > *:last-child {
  margin-bottom: 0;
}
</style>



